import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  Shield, Eye, Camera, Zap, Smartphone, QrCode, Bell, AlertTriangle, Cloud,
  Phone, Mail, MapPin, Menu, X, ChevronRight, Star, Building, Users, Clock,
  CheckCircle, ArrowRight, ArrowUp, Instagram, Facebook
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import AdminPage from "@/AdminPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "5518997181244";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Gostaria de solicitar um orçamento.`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_debd93df-42d8-4618-80e3-85efe6df3bc8/artifacts/m1wio4ud_LOGO%20AGUI%20SEM%20FUNDO-PNG.png";
const VIDEO_URL = "https://customer-assets.emergentagent.com/job_debd93df-42d8-4618-80e3-85efe6df3bc8/artifacts/6jxek8a9_institucional-AGUIA.mp4";

// ========== UTILITY COMPONENTS ==========

const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40, filter: "blur(8px)" }}
      animate={isInView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Typewriter Effect
const Typewriter = ({ words, className = "" }) => {
  const [index, setIndex] = useState(0);
  const [text, setText] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const word = words[index];
    const timeout = setTimeout(() => {
      if (!deleting) {
        setText(word.substring(0, text.length + 1));
        if (text.length + 1 === word.length) {
          setTimeout(() => setDeleting(true), 2000);
        }
      } else {
        setText(word.substring(0, text.length - 1));
        if (text.length === 0) {
          setDeleting(false);
          setIndex((i) => (i + 1) % words.length);
        }
      }
    }, deleting ? 40 : 80);
    return () => clearTimeout(timeout);
  }, [text, deleting, index, words]);

  return (
    <span className={className}>
      {text}
      <span className="typewriter-cursor" />
    </span>
  );
};

// Counter Animation
const CountUp = ({ end, suffix = "", duration = 2 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const endNum = parseInt(end.replace(/\D/g, ""));
    if (!endNum) { setCount(end); return; }
    const step = endNum / (duration * 60);
    const timer = setInterval(() => {
      start += step;
      if (start >= endNum) { clearInterval(timer); setCount(endNum); }
      else setCount(Math.floor(start));
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [isInView, end, duration]);

  return <span ref={ref}>{typeof count === "number" ? count : end}{suffix}</span>;
};

// Floating Particles Canvas
const ParticlesCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const particles = [];
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);

    for (let i = 0; i < 35; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 2 + 0.5,
        dx: (Math.random() - 0.5) * 0.3,
        dy: (Math.random() - 0.5) * 0.3,
        opacity: Math.random() * 0.5 + 0.1,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212, 175, 55, ${p.opacity})`;
        ctx.fill();
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);

  return <canvas ref={canvasRef} className="particles-canvas" />;
};

// Custom Cursor
const CustomCursor = () => {
  const cursorRef = useRef(null);

  useEffect(() => {
    const cursor = cursorRef.current;
    if (!cursor) return;
    const move = (e) => { cursor.style.left = e.clientX + "px"; cursor.style.top = e.clientY + "px"; };
    const addHover = () => cursor.classList.add("hovering");
    const removeHover = () => cursor.classList.remove("hovering");

    document.addEventListener("mousemove", move);
    document.querySelectorAll("a, button, [role=button], input, textarea, select").forEach((el) => {
      el.addEventListener("mouseenter", addHover);
      el.addEventListener("mouseleave", removeHover);
    });
    return () => document.removeEventListener("mousemove", move);
  }, []);

  return <div ref={cursorRef} className="custom-cursor" />;
};

// Back to Top
const BackToTop = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 500);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!show) return null;
  return (
    <motion.button
      className="back-to-top"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Voltar ao topo"
      data-testid="back-to-top-btn"
    >
      <ArrowUp className="w-5 h-5 text-yellow-500" />
    </motion.button>
  );
};

// ========== HEADER ==========
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: "Home", href: "#home" },
    { label: "Serviços", href: "#servicos" },
    { label: "Sobre Nós", href: "#sobre" },
    { label: "Contato", href: "#contato" },
  ];

  return (
    <header className={`header ${isScrolled ? "header-scrolled" : ""}`} data-testid="header">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
        <div className="flex items-center justify-between">
          <a href="#home" className="flex items-center" data-testid="logo">
            <img src={LOGO_URL} alt="Águia Monitoramentos" className="h-14 w-auto" data-testid="logo-image" />
          </a>
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {navItems.map((item) => (
              <a key={item.label} href={item.href} className="nav-link">{item.label}</a>
            ))}
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-primary ml-4" data-testid="header-whatsapp-btn">
              <Phone className="w-4 h-4" /> WhatsApp
            </a>
          </nav>
          <button className="md:hidden p-2" onClick={() => setIsMobileMenuOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="w-6 h-6 text-yellow-600" />
          </button>
        </div>
      </div>
      {isMobileMenuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <div className="flex justify-between items-center mb-8">
            <img src={LOGO_URL} alt="Águia Monitoramentos" className="h-12 w-auto" />
            <button onClick={() => setIsMobileMenuOpen(false)}>
              <X className="w-6 h-6 text-yellow-600" />
            </button>
          </div>
          <nav className="flex flex-col gap-4">
            {navItems.map((item) => (
              <a key={item.label} href={item.href} className="text-lg font-medium text-gray-300 py-2" onClick={() => setIsMobileMenuOpen(false)}>
                {item.label}
              </a>
            ))}
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-primary justify-center mt-4">
              <Phone className="w-4 h-4" /> WhatsApp
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

// ========== HERO SECTION ==========
const HeroSection = () => {
  const imgRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) / 25;
    const y = (e.clientY - rect.top - rect.height / 2) / 25;
    imgRef.current.style.transform = `perspective(800px) rotateY(${x}deg) rotateX(${-y}deg)`;
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (imgRef.current) imgRef.current.style.transform = "perspective(800px) rotateY(0deg) rotateX(0deg)";
  }, []);

  return (
    <section id="home" className="hero-section pt-24 md:pt-0" data-testid="hero-section">
      <div className="hero-overlay" style={{ background: "linear-gradient(135deg, rgba(10,10,10,0.95) 0%, rgba(10,10,10,0.85) 100%)" }} />

      <div className="max-w-7xl mx-auto px-4 md:px-6 min-h-screen flex items-center relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center py-20">
          <motion.div className="text-white" initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
            <motion.div className="badge-glow mb-6" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 }}>
              <Shield className="w-4 h-4" /> +18 Anos de Experiência
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Segurança{" "}
              <span className="gold-gradient gold-glow">Inteligente</span>
              <br />
              <Typewriter words={["24 Horas para Você", "Para seu Condomínio", "Para sua Empresa"]} className="gold-gradient" />
            </h1>

            <p className="text-lg md:text-xl text-gray-400 mb-8 max-w-lg">
              Proteção completa para seu condomínio e empresa. Economia de até
              <span className="gold-gradient font-bold"> 50%</span> com portaria remota e tecnologia de ponta.
            </p>

            <motion.div className="flex flex-col sm:flex-row gap-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-primary text-center justify-center" data-testid="hero-whatsapp-btn">
                <Phone className="w-5 h-5" /> Solicitar Orçamento
              </a>
              <a href="#servicos" className="btn-secondary text-center justify-center" data-testid="hero-services-btn">
                Conheça Nossos Serviços <ChevronRight className="w-5 h-5" />
              </a>
            </motion.div>

            <motion.div className="flex flex-wrap gap-6 mt-10 pt-10 border-t border-neutral-700/50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
              {["Monitoramento 24h", "Resposta Imediata", "Tecnologia Avançada"].map((t) => (
                <div key={t} className="flex items-center gap-2 text-gray-400">
                  <CheckCircle className="w-5 h-5 text-yellow-600" /> <span>{t}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            className="hidden md:block relative"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <div ref={imgRef} className="relative rounded-2xl overflow-hidden shadow-2xl transition-transform duration-200">
              <img
                src="https://images.unsplash.com/photo-1558002038-1055907df827?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxzbWFydCUyMGhvbWUlMjBzZWN1cml0eSUyMGFwcCUyMHBob25lJTIwaGFuZHxlbnwwfHx8fDE3NzAzOTE5ODJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Sistema de Segurança"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute inset-0 border-2 border-transparent rounded-2xl" style={{ boxShadow: "inset 0 0 30px rgba(212,175,55,0.1)" }} />
            </div>

            <motion.div className="absolute -bottom-6 -left-6 glass p-4 rounded-xl" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.8, type: "spring" }}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #C5A028, #F0D060)" }}>
                  <Eye className="w-6 h-6 text-black" />
                </div>
                <div className="text-white">
                  <p className="font-bold text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    <CountUp end="2000" suffix="+" />
                  </p>
                  <p className="text-sm text-gray-400">Clientes Protegidos</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const PORTARIA_VIDEO_URL = "https://customer-assets.emergentagent.com/job_debd93df-42d8-4618-80e3-85efe6df3bc8/artifacts/rxznmdww_AGUIA-AN%C3%9ANCIO-PORTARIA%20REMOTA.mp4";

// ========== SERVICES ==========
const ServicesSection = () => {
  const services = [
    { icon: Building, title: "Portaria Remota", description: "Controle de acesso 24h por operadores especializados. Reduza custos em até 50%." },
    { icon: Eye, title: "Monitoramento 24h", description: "Vigilância contínua com gravação de câmeras e resposta rápida a emergências." },
    { icon: Camera, title: "Câmeras e Alarmes", description: "Câmeras com visão noturna, sensores de movimento e alarmes de última geração." },
    { icon: Zap, title: "Cerca Elétrica", description: "Proteção perimetral completa com cerca elétrica e concertina." },
  ];

  return (
    <section id="servicos" className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <AnimatedSection className="text-center mb-16">
          <span className="gold-gradient font-semibold uppercase tracking-wider text-sm">Nossos Serviços</span>
          <h2 className="section-title mt-2">Soluções Completas em Segurança</h2>
          <p className="section-subtitle mx-auto">Tecnologia de ponta e equipe especializada para proteger seu patrimônio.</p>
        </AnimatedSection>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s, i) => (
            <AnimatedSection key={i} delay={i * 0.1}>
              <motion.div
                className="glass-card h-full"
                whileHover={{ y: -10, scale: 1.03 }}
                transition={{ duration: 0.3 }}
                data-testid={`service-card-${i}`}
              >
                <div className="icon-wrapper-gold icon-float">
                  <s.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold gold-gradient mb-3">{s.title}</h3>
                <p className="text-gray-400 leading-relaxed text-sm">{s.description}</p>
              </motion.div>
            </AnimatedSection>
          ))}
        </div>

        {/* Portaria Remota Video */}
        <AnimatedSection className="mt-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl border border-neutral-800">
              <video
                controls loop playsInline preload="auto"
                className="w-full rounded-2xl"
                style={{ aspectRatio: "16/9" }}
                data-testid="portaria-remota-video"
              >
                <source src={PORTARIA_VIDEO_URL} type="video/mp4" />
              </video>
            </div>
            <div>
              <span className="gold-gradient font-semibold uppercase tracking-wider text-sm">Destaque</span>
              <h3 className="text-3xl md:text-4xl font-bold mt-2 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Portaria <span className="gold-gradient gold-glow">Remota</span>
              </h3>
              <p className="text-gray-400 leading-relaxed mb-6">
                Reduza custos em até <strong className="gold-gradient">50%</strong> com nossa portaria remota.
                Operadores especializados monitoram e controlam o acesso do seu condomínio 24 horas por dia,
                com tecnologia de ponta em vídeo e comunicação.
              </p>
              <ul className="space-y-3 mb-8">
                {["Controle de acesso por vídeo 24h", "Operadores treinados e especializados", "Economia real sem perder segurança", "Tecnologia de ponta em comunicação"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" /> {item}
                  </li>
                ))}
              </ul>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="portaria-whatsapp-btn">
                <Phone className="w-5 h-5" /> Solicitar Orçamento
              </a>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// ========== BENEFITS ==========
const BenefitsSection = () => {
  const benefits = [
    { icon: Smartphone, title: "Controle Total pelo App", desc: "Monitore e controle sua segurança pelo celular" },
    { icon: QrCode, title: "Acesso via QR Code", desc: "Liberação rápida e segura de visitantes" },
    { icon: AlertTriangle, title: "Sensor de Alerta para Carona", desc: "Detecta entrada não autorizada de veículos" },
    { icon: Bell, title: "Acionamento Silencioso", desc: "Botão de pânico discreto para emergências" },
    { icon: Cloud, title: "Gravação em Nuvem", desc: "Imagens armazenadas com segurança remota" },
    { icon: Clock, title: "Suporte 24/7", desc: "Atendimento técnico a qualquer momento" },
  ];

  const imgRef = useRef(null);
  const imgInView = useInView(imgRef, { once: true, margin: "-100px" });

  return (
    <section className="py-20 md:py-32 bg-[#080808]" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <AnimatedSection>
            <div className="relative" ref={imgRef}>
              <div className="rounded-2xl overflow-hidden shadow-xl border border-neutral-800">
                <video
                  controls loop playsInline preload="auto"
                  poster="https://customer-assets.emergentagent.com/job_bda06a56-e60b-4a56-8417-dce4204bfbaf/artifacts/9ejd952z_image.png"
                  className="w-full rounded-2xl"
                  style={{ aspectRatio: "16/9" }}
                  data-testid="nossa-base-video"
                >
                  <source src={VIDEO_URL} type="video/mp4" />
                </video>
              </div>
              <motion.div
                className="absolute -bottom-4 -right-4 px-6 py-3 rounded-xl font-bold shadow-lg"
                style={{ background: "linear-gradient(135deg, #C5A028, #F0D060)", color: "#000" }}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, type: "spring" }}
              >
                Nossa Base
              </motion.div>
            </div>
          </AnimatedSection>

          <div>
            <AnimatedSection>
              <span className="gold-gradient font-semibold uppercase tracking-wider text-sm">Diferenciais</span>
              <h2 className="section-title mt-2 mb-8">Por que Escolher a Águia?</h2>
            </AnimatedSection>
            <div className="grid gap-4">
              {benefits.map((b, i) => (
                <AnimatedSection key={i} delay={i * 0.08}>
                  <motion.div className="benefit-card" whileHover={{ x: 8 }} data-testid={`benefit-item-${i}`}>
                    <div className="w-12 h-12 bg-yellow-600/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <b.icon className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <h4 className="font-bold gold-gradient">{b.title}</h4>
                      <p className="text-sm text-gray-500">{b.desc}</p>
                    </div>
                  </motion.div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ========== ABOUT / STATS ==========
const AboutSection = () => {
  const stats = [
    { number: "18", suffix: "+", label: "Anos de Mercado" },
    { number: "2000", suffix: "+", label: "Clientes Protegidos" },
    { number: "24", suffix: "/7", label: "Monitoramento Contínuo" },
    { number: "100", suffix: "%", label: "Satisfação Garantida" },
  ];

  return (
    <section id="sobre" className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <AnimatedSection>
            <span className="gold-gradient font-semibold uppercase tracking-wider text-sm">Sobre Nós</span>
            <h2 className="section-title mt-2 mb-6">Tradição e Tecnologia em Segurança</h2>
            <p className="text-gray-400 text-lg mb-6 leading-relaxed">
              A <strong className="gold-gradient">Águia Monitoramentos</strong> atua há mais de 18 anos no mercado
              de segurança eletrônica, sendo referência em Birigui, Araçatuba e toda a região noroeste paulista.
            </p>
            <p className="text-gray-500 mb-8 leading-relaxed">
              Nossa equipe é formada por profissionais especializados e treinados para oferecer soluções
              personalizadas em segurança patrimonial, utilizando tecnologia de ponta e atendimento humanizado.
            </p>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="about-whatsapp-btn">
              <Phone className="w-5 h-5" /> Fale Conosco
            </a>
          </AnimatedSection>

          <div className="grid grid-cols-2 gap-6">
            {stats.map((s, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <motion.div
                  className="stat-card"
                  whileHover={{ scale: 1.05 }}
                  data-testid={`stat-item-${i}`}
                >
                  <p className="stat-number"><CountUp end={s.number} suffix={s.suffix} /></p>
                  <p className="text-gray-500 font-medium text-sm">{s.label}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ========== TESTIMONIALS ==========
const TestimonialsSection = () => {
  const testimonials = [
    { name: "Carlos Alberto", role: "Síndico - Condomínio Jardins", text: "A Águia Monitoramentos transformou a segurança do nosso condomínio. Reduzimos custos em 45% com a portaria remota e a qualidade do serviço é impecável.", rating: 5 },
    { name: "Maria Fernanda", role: "Empresária", text: "Profissionais extremamente competentes. O sistema de câmeras e alarmes instalado na minha empresa funciona perfeitamente. Recomendo a todos!", rating: 5 },
    { name: "José Ricardo", role: "Síndico - Residencial Premium", text: "Mais de 3 anos como cliente e nunca tivemos problemas. O suporte 24h realmente funciona e a equipe técnica é muito prestativa.", rating: 5 },
  ];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setIdx((p) => (p + 1) % testimonials.length), 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <section className="py-20 md:py-32 bg-[#080808]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <AnimatedSection className="text-center mb-16">
          <span className="gold-gradient font-semibold uppercase tracking-wider text-sm">Depoimentos</span>
          <h2 className="section-title mt-2">O que Nossos Clientes Dizem</h2>
        </AnimatedSection>

        <AnimatedSection className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              className="testimonial-glass"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.4 }}
              data-testid="testimonial-card"
            >
              <div className="flex gap-1 mb-4">
                {[...Array(testimonials[idx].rating)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <Star className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                  </motion.div>
                ))}
              </div>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed italic">
                "{testimonials[idx].text}"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ border: "2px solid var(--gold)", background: "rgba(212,175,55,0.1)" }}>
                  <Users className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="font-bold gold-gradient">{testimonials[idx].name}</p>
                  <p className="text-sm text-gray-500">{testimonials[idx].role}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} className={`progress-dot ${i === idx ? "active" : ""}`} data-testid={`testimonial-dot-${i}`}>
                {i === idx && <div className="progress-fill" />}
              </button>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// ========== CONTACT ==========
const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Mensagem enviada com sucesso!");
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
      setTimeout(() => setSubmitted(false), 3000);
    } catch {
      toast.error("Erro ao enviar. Tente pelo WhatsApp.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contato" className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-16">
          <AnimatedSection>
            <span className="gold-gradient font-semibold uppercase tracking-wider text-sm">Contato</span>
            <h2 className="section-title mt-2 mb-4">Solicite seu Orçamento</h2>
            <p className="text-gray-500 mb-8">Preencha o formulário e nossa equipe entrará em contato.</p>

            <form onSubmit={handleSubmit} className="contact-form-premium" data-testid="contact-form">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium gold-gradient mb-2">Nome Completo *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Seu nome" required className="input-gold" data-testid="contact-name-input" />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium gold-gradient mb-2">E-mail *</label>
                    <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="seu@email.com" required className="input-gold" data-testid="contact-email-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium gold-gradient mb-2">Telefone/WhatsApp *</label>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(18) 99999-9999" required className="input-gold" data-testid="contact-phone-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium gold-gradient mb-2">Serviço de Interesse</label>
                  <select value={formData.service} onChange={(e) => setFormData({ ...formData, service: e.target.value })} className="input-gold" style={{ appearance: "none" }} data-testid="contact-service-select">
                    <option value="">Selecione um serviço</option>
                    <option value="portaria-remota">Portaria Remota</option>
                    <option value="monitoramento-24h">Monitoramento 24h</option>
                    <option value="cameras-alarmes">Câmeras e Alarmes</option>
                    <option value="cerca-eletrica">Cerca Elétrica / Concertina</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium gold-gradient mb-2">Mensagem *</label>
                  <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="Descreva sua necessidade..." required rows={4} className="input-gold resize-none" data-testid="contact-message-textarea" />
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-14 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all"
                  style={{ background: submitted ? "#22c55e" : "linear-gradient(135deg, #C5A028, #D4AF37, #F0D060)", color: "#000" }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="contact-submit-btn"
                >
                  <AnimatePresence mode="wait">
                    {submitted ? (
                      <motion.div key="check" initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex items-center gap-2">
                        <CheckCircle className="w-6 h-6" /> Enviado!
                      </motion.div>
                    ) : isSubmitting ? (
                      <motion.span key="loading">Enviando...</motion.span>
                    ) : (
                      <motion.span key="default" className="flex items-center gap-2">
                        Enviar Solicitação <ArrowRight className="w-5 h-5" />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.button>
              </div>
            </form>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <div className="rounded-3xl p-8 md:p-10 text-white h-full" style={{ background: "#0F0F0F", border: "1px solid #1A1A1A" }}>
              <h3 className="text-2xl font-bold mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>Informações de Contato</h3>
              <div className="space-y-6">
                {[
                  { icon: Phone, title: "Telefone", lines: ["(18) 3634-2528", "(18) 99718-1244 (WhatsApp)"] },
                  { icon: Mail, title: "E-mail", lines: ["financeiro@aguiamonitoramentos.com.br"] },
                  { icon: MapPin, title: "Birigui - SP", lines: ["Av. Nove de Julho, 1797", "Jardim Stabile"] },
                  { icon: MapPin, title: "Araçatuba - SP", lines: ["Rua Cussy de Almeida Jr. 1423", "Centro"] },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(212,175,55,0.1)" }}>
                      <item.icon className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">{item.title}</p>
                      {item.lines.map((l, j) => <p key={j} className="text-gray-400 text-sm">{l}</p>)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-8 border-t border-neutral-800">
                <p className="font-semibold mb-4">Nossas Localizações</p>
                <div className="grid gap-4">
                  <div className="rounded-xl overflow-hidden border border-neutral-800">
                    <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3718.3!2d-50.342!3d-21.288!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjHCsDE3JzE2LjgiUyA1MMKwMjAnMzEuMiJX!5e0!3m2!1spt-BR!2sbr!4v1" width="100%" height="160" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="Birigui" data-testid="google-maps-birigui" />
                  </div>
                  <div className="rounded-xl overflow-hidden border border-neutral-800">
                    <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3722.5!2d-50.432!3d-21.209!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjHCsDEyJzMyLjQiUyA1MMKwMjUnNTUuNSJX!5e0!3m2!1spt-BR!2sbr!4v1" width="100%" height="160" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="Araçatuba" data-testid="google-maps-aracatuba" />
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-neutral-800">
                <p className="font-semibold mb-4">Siga-nos nas Redes Sociais</p>
                <div className="flex gap-4">
                  {[
                    { href: "https://www.instagram.com/aguiamonitoramentosoficial/", icon: Instagram, id: "social-instagram" },
                    { href: "https://www.facebook.com/aguiamonitoramento/", icon: Facebook, id: "social-facebook" },
                  ].map((s) => (
                    <a key={s.id} href={s.href} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl flex items-center justify-center transition-all hover:scale-110" style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.2)" }} data-testid={s.id}>
                      <s.icon className="w-5 h-5 text-yellow-600" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// ========== CTA ==========
const CTASection = () => (
  <section className="cta-section py-20 md:py-24" data-testid="cta-section">
    <div className="max-w-7xl mx-auto px-4 md:px-6 text-center relative z-10">
      <AnimatedSection>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
          Quer mais <span className="gold-gradient gold-glow">Segurança</span> e Economia?
        </h2>
        <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
          Entre em contato agora e descubra como proteger seu patrimônio com a melhor relação custo-benefício.
        </p>
        <motion.a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary inline-flex text-lg px-10 py-5"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.97 }}
          data-testid="cta-whatsapp-btn"
        >
          <Phone className="w-6 h-6" /> Falar pelo WhatsApp
        </motion.a>
      </AnimatedSection>
    </div>
  </section>
);

// ========== FOOTER ==========
const Footer = () => (
  <footer className="footer py-16" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 md:px-6">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img src={LOGO_URL} alt="Águia Monitoramentos" className="h-16 w-auto" data-testid="footer-logo-image" />
          </div>
          <p className="text-gray-500 mb-6 max-w-md">
            Há mais de 18 anos oferecendo soluções completas em segurança eletrônica para condomínios e empresas em Birigui, Araçatuba e região.
          </p>
          <div className="flex gap-4">
            {[
              { href: "https://www.instagram.com/aguiamonitoramentosoficial/", icon: Instagram },
              { href: "https://www.facebook.com/aguiamonitoramento/", icon: Facebook },
            ].map((s, i) => (
              <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg flex items-center justify-center transition-all hover:scale-110" style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.15)" }}>
                <s.icon className="w-5 h-5 text-yellow-600" />
              </a>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4">Links Rápidos</h4>
          <ul className="space-y-3">
            {[{ l: "Home", h: "#home" }, { l: "Serviços", h: "#servicos" }, { l: "Sobre Nós", h: "#sobre" }, { l: "Contato", h: "#contato" }].map((i) => (
              <li key={i.l}><a href={i.h}>{i.l}</a></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4">Contato</h4>
          <ul className="space-y-3 text-gray-500">
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-yellow-600" />(18) 3634-2528</li>
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-yellow-600" />(18) 99718-1244</li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-yellow-600" />financeiro@aguiamonitoramentos.com.br</li>
          </ul>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-8 py-8 border-t border-neutral-800">
        {[
          { city: "Birigui - SP", addr: "Av. Nove de Julho, 1797 - Jardim Stabile" },
          { city: "Araçatuba - SP", addr: "Rua Cussy de Almeida Jr. 1423 - Centro" },
        ].map((a, i) => (
          <div key={i} className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
            <div className="text-gray-500">
              <p className="text-white font-semibold">{a.city}</p>
              <p>{a.addr}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="pt-8 border-t border-neutral-800 text-center">
        <p className="text-gray-500 text-sm mb-2">AGUIA MONITORAMENTO E ALARMES EIRELI — CNPJ: 09.045.484/0001-60</p>
        <p className="text-gray-600 text-sm">© {new Date().getFullYear()} Águia Monitoramentos. Todos os direitos reservados.</p>
      </div>
    </div>
  </footer>
);

// ========== WHATSAPP FLOAT ==========
const WhatsAppFloat = () => (
  <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="whatsapp-float" aria-label="WhatsApp" data-testid="whatsapp-float-btn">
    <svg viewBox="0 0 32 32" className="w-7 h-7" style={{ fill: "#D4AF37" }}>
      <path d="M16 2C8.28 2 2 8.28 2 16c0 2.47.65 4.78 1.77 6.79L2 30l7.45-1.73C11.28 29.33 13.57 30 16 30c7.72 0 14-6.28 14-14S23.72 2 16 2zm0 25.5c-2.17 0-4.22-.58-6.02-1.64l-.43-.26-4.42 1.03 1.07-4.28-.28-.45A11.44 11.44 0 014.5 16C4.5 9.66 9.66 4.5 16 4.5S27.5 9.66 27.5 16 22.34 27.5 16 27.5zm6.28-8.5c-.34-.17-2.03-1-2.35-1.12-.31-.11-.54-.17-.77.17-.23.34-.89 1.12-1.09 1.35-.2.23-.4.26-.74.09-.34-.17-1.44-.53-2.75-1.69-1.02-.9-1.7-2.01-1.9-2.35-.2-.34-.02-.52.15-.69.15-.15.34-.4.51-.6.17-.2.23-.34.34-.57.11-.23.06-.43-.03-.6-.09-.17-.77-1.86-1.06-2.55-.28-.67-.56-.58-.77-.59-.2-.01-.43-.01-.66-.01-.23 0-.6.09-.91.43-.31.34-1.2 1.17-1.2 2.85s1.23 3.31 1.4 3.54c.17.23 2.42 3.7 5.87 5.19.82.35 1.46.56 1.96.72.82.26 1.57.22 2.16.13.66-.1 2.03-.83 2.31-1.63.29-.8.29-1.48.2-1.63-.09-.14-.31-.23-.66-.4z"/>
    </svg>
  </a>
);

// ========== LANDING PAGE ==========
const LandingPage = () => (
  <div className="min-h-screen" style={{ background: "#0A0A0A" }}>
    <ParticlesCanvas />
    <CustomCursor />
    <Header />
    <main>
      <HeroSection />
      <ServicesSection />
      <BenefitsSection />
      <AboutSection />
      <TestimonialsSection />
      <ContactSection />
      <CTASection />
    </main>
    <Footer />
    <WhatsAppFloat />
    <BackToTop />
  </div>
);

// ========== APP ==========
function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
